/**
 * **FI-17 (D7)** — Service SSOT per le mosse multiclasse del PG.
 *
 * Una mossa multiclasse permette al PG di "pescare" una mossa da un'altra
 * classe (manuale V1 rr. 1764-1773). Il service computa puro:
 *  - il **pool delle classi pescabili** dato un `MulticlassConfig` + lo
 *    stato corrente del PG (archetypeId, secondaryArchetypeId, moves
 *    possedute, levelUpHistory per vincolo storico Eclettico/Poliedrico)
 *  - il **pool delle mosse-target** dato una `sourceClassId` + filtro
 *    nativo `tags: ['multiclasse']` escluse + prereq soddisfatti + non
 *    già possedute + tipo compatibile col livello effettivo
 *  - il **livello effettivo del PG** nella `sourceClassId` ai fini della
 *    scelta (applica `levelAdjustment.delta` con eccezioni)
 *  - i **livelli accumulati per classe** del PG (`getClassLevels`),
 *    derivato runtime da `levelUpHistory[].multiclassPick` (no campo
 *    persistito — SSOT history, decisione Q3 = B 2026-05-14)
 *
 * **Manuale rr. 1761-1768** — regola del livello-in-classe:
 *  1. Prima volta che entri in una classe X via multiclasse → considerati
 *     PG di **1° livello in X**.
 *  2. A ogni level-up successivo del PG, **tutti** i livelli accumulati
 *     nelle altre classi aumentano di 1.
 *
 * Formula derivata: `classLevels[X] = character.level - (entryLevel[X] - 1)`
 * dove `entryLevel[X]` è il `levelReached` del primo record di
 * `levelUpHistory` con `multiclassPick.sourceClassId === X`.
 */

import { findMoveByIdGlobal, movesByClass } from '../data/moves';
import type { Character } from '../schemas/character';
import type { ClassDefinition } from '../schemas/classDefinition';
import { CLASS_IDS, type ClassId } from '../schemas/classId';
import type { Move, Prerequisite } from '../schemas/move';
import type { MulticlassConfig } from '../schemas/multiclass';
import { arePrerequisitesSatisfied } from './levelUp';

// ---------------------------------------------------------------------------
// Pool delle classi pescabili
// ---------------------------------------------------------------------------

/**
 * Calcola il pool delle **classi pescabili** dato un `MulticlassConfig`
 * + lo stato corrente del PG. Applica nell'ordine:
 *  1. `picks` → base pool (`fixed-classes`, `any-other-class`, oppure
 *     `from-extensions-only` introdotto da FI-37 con pool base vuoto)
 *  2. `extendsByArchetype` → +extraClasses se `character.archetypeId` OR
 *     `secondaryArchetypeId` (FI-1 esteso, *Infanzia Complessa*) match
 *  3. `extendsByMove` → +extraClasses se `character.moves` contiene `moveId`
 *  4. `excludesClassesPickedInMoves` → −classi pescate in mosse multiclasse
 *     precedenti (vincolo storico Bardo *Eclettico* / *Poliedrico*); lookup
 *     via `character.levelUpHistory[].multiclassPick.sourceClassId`
 *  5. Esclude SEMPRE la classe primaria del PG (manuale: "altra classe")
 *
 * Output ordinato per `CLASS_IDS` (alfabetico stabile) e deduplicato.
 */
export function getMulticlassClassPool(
  character: Character,
  config: MulticlassConfig,
  classDef: ClassDefinition,
): readonly ClassId[] {
  const pool = new Set<ClassId>();
  addBasePool(pool, config, classDef);
  addArchetypeExtensions(pool, character, config);
  addMoveExtensions(pool, character, config);
  addMoveChoiceExtensions(pool, character, config);
  if (config.excludesClassesPickedInMoves) {
    const excluded = collectExcludedClasses(character, config.excludesClassesPickedInMoves);
    for (const c of excluded) pool.delete(c);
  }
  pool.delete(classDef.id);
  return CLASS_IDS.filter((c) => pool.has(c));
}

function addBasePool(
  pool: Set<ClassId>,
  config: MulticlassConfig,
  classDef: ClassDefinition,
): void {
  if (config.picks.kind === 'fixed-classes') {
    for (const c of config.picks.classes) pool.add(c);
  } else if (config.picks.kind === 'any-other-class') {
    for (const c of CLASS_IDS) {
      if (c !== classDef.id) pool.add(c);
    }
  }
  // kind === 'from-extensions-only' (FI-37 + FI-43 + FI-44): pool base vuoto,
  // popolato solo dalle extensions sotto.
}

function addArchetypeExtensions(
  pool: Set<ClassId>,
  character: Character,
  config: MulticlassConfig,
): void {
  if (!config.extendsByArchetype) return;
  const activeArchetypes = new Set<string>([character.archetypeId]);
  if (character.secondaryArchetypeId !== undefined) {
    activeArchetypes.add(character.secondaryArchetypeId);
  }
  for (const ext of config.extendsByArchetype) {
    if (activeArchetypes.has(ext.archetypeId)) {
      for (const c of ext.extraClasses) pool.add(c);
    }
  }
}

function addMoveExtensions(
  pool: Set<ClassId>,
  character: Character,
  config: MulticlassConfig,
): void {
  if (!config.extendsByMove) return;
  const ownedMoves = new Set(character.moves.map((m) => m.id));
  for (const ext of config.extendsByMove) {
    if (ownedMoves.has(ext.moveId)) {
      for (const c of ext.extraClasses) pool.add(c);
    }
  }
}

/**
 * FI-44 (2026-05-15): più granulare di `extendsByMove` — l'estensione
 * attiva dipende da quale `choiceId` il PG ha in `selectedChoices` della
 * mossa-source. Caso pilota: *Seconda Laurea* del Chierico.
 */
function addMoveChoiceExtensions(
  pool: Set<ClassId>,
  character: Character,
  config: MulticlassConfig,
): void {
  if (!config.extendsByMoveChoice) return;
  const movesById = new Map(character.moves.map((m) => [m.id, m]));
  for (const ext of config.extendsByMoveChoice) {
    const moveRef = movesById.get(ext.moveId);
    if (!moveRef) continue;
    for (const choiceId of moveRef.selectedChoices) {
      const extraClasses = ext.classesByChoice[choiceId];
      if (extraClasses) {
        for (const c of extraClasses) pool.add(c);
      }
    }
  }
}

/**
 * Raccoglie le `sourceClassId` selezionate in passati `multiclassPick`
 * delle mosse referenziate. Helper per `excludesClassesPickedInMoves`.
 */
function collectExcludedClasses(
  character: Character,
  excludedMoveIds: readonly string[],
): readonly ClassId[] {
  const excludedSet = new Set(excludedMoveIds);
  const result: ClassId[] = [];
  for (const record of character.levelUpHistory) {
    if (excludedSet.has(record.moveAdded) && record.multiclassPick !== undefined) {
      result.push(record.multiclassPick.sourceClassId);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Pool delle mosse target nella classe selezionata
// ---------------------------------------------------------------------------

/**
 * Calcola il pool delle **mosse-target** disponibili nella `sourceClassId`,
 * dato lo stato corrente del PG + il config multiclasse + il livello
 * effettivo (per filtro `advanced_2_5`/`advanced_6_10`).
 *
 * Filtri applicati (nell'ordine):
 *  1. Mosse della `sourceClassId` nel catalogo (`movesByClass[sourceClassId]`)
 *  2. **Filtro nativo** `!tags.includes('multiclasse')` — manuale: «Non
 *     prendere mosse multiclasse»
 *  3. Tipo ∈ {`'starting'`, `'advanced_2_5'`, `'advanced_6_10'`} — escluse
 *     `class_special` (archetipi/istinti/stili difesa, scelti diversamente),
 *     `base`/`system` (universali, non scelte), `common` (Infanzia
 *     Complessa, multiclasse-post-10°, scelte come `'main'`)
 *  4. Mossa non già posseduta dal PG
 *  5. Prerequisites soddisfatti (cross-class, riusa `arePrerequisitesSatisfied`)
 *  6. Tipo compatibile col livello effettivo:
 *     - `'starting'`: sempre OK
 *     - `'advanced_2_5'`: solo se effective ≥ 2
 *     - `'advanced_6_10'`: solo se effective ≥ 6
 *
 * **NB**: non filtra per `MoveSchema.replaces` perché le mosse-target
 * multiclasse della classe-source non possono soppiantare mosse della
 * classe primaria del PG (semantica del manuale). Se una mossa-target ha
 * `replaces` non vuoto, il filtro generico (prereq) ne richiederebbe la
 * mossa-soppiantata posseduta, ma il PG non ne ha mai una mossa della
 * classe-source. Quindi nelle pratica `replaces` esclude la mossa.
 */
export function getMulticlassMovePool(
  character: Character,
  sourceClassId: ClassId,
  config: MulticlassConfig,
  classDef: ClassDefinition,
): readonly Move[] {
  const ownedIds = new Set(character.moves.map((m) => m.id));
  const effectiveLevel = getEffectiveLevelForPick(character, sourceClassId, config, classDef);
  const candidates = movesByClass[sourceClassId] ?? [];

  return candidates.filter((move) => {
    if (move.tags.includes('multiclasse')) return false;
    if (move.type !== 'starting' && move.type !== 'advanced_2_5' && move.type !== 'advanced_6_10') {
      return false;
    }
    if (ownedIds.has(move.id)) return false;
    if (!arePrerequisitesSatisfied(move.prerequisites, ownedIds)) return false;
    if (move.type === 'advanced_2_5' && effectiveLevel < 2) return false;
    if (move.type === 'advanced_6_10' && effectiveLevel < 6) return false;
    // FI-17: mosse target con `replaces` non vuoto richiederebbero che il
    // PG abbia la mossa-soppiantata della classe-source — impossibile per
    // la prima entry in quella classe. Filtro implicito via prereq, ma
    // espliciamo per sicurezza.
    if (move.replaces.length > 0 && !move.replaces.some((id) => ownedIds.has(id))) {
      return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Livello effettivo del PG nella classe-source
// ---------------------------------------------------------------------------

/**
 * Livello "virtuale" del PG nella `sourceClassId` ai fini della scelta
 * della mossa-target. Applica:
 *  1. `baseLevel` = se prima entry → 1 (manuale r. 1761), altrimenti
 *     `classLevels[sourceClassId]` derivato da history
 *  2. `delta` = `config.levelAdjustment?.delta ?? 0`, azzerato se l'eccezione
 *     matcha la `sourceClassId`
 *
 * Output: max(1, baseLevel + delta). Non scende sotto 1 (filtro nativo:
 * starting comunque disponibili).
 */
export function getEffectiveLevelForPick(
  character: Character,
  sourceClassId: ClassId,
  config: MulticlassConfig,
  classDef: ClassDefinition,
): number {
  const classLevels = getClassLevels(character);
  // Per la classe primaria del PG il livello è character.level — ma in
  // pratica `sourceClassId` non dovrebbe mai essere la classe primaria
  // (escluso dal pool). Default conservativo: character.level.
  let baseLevel: number;
  if (sourceClassId === classDef.id) {
    baseLevel = character.level;
  } else if (classLevels[sourceClassId] !== undefined) {
    baseLevel = classLevels[sourceClassId];
  } else {
    baseLevel = 1; // prima entry — manuale r. 1761
  }

  if (config.levelAdjustment === undefined) return baseLevel;

  // Applicazione eccezioni: se la sourceClassId rientra nelle eccezioni
  // sourceClassIds, il delta non si applica.
  const exceptions = config.levelAdjustment.exceptions;
  if (exceptions?.sourceClassIds?.includes(sourceClassId)) {
    return baseLevel;
  }
  // Eccezione moveTags applicata per-mossa, non a livello classe → gestita
  // nel pool filter per le mosse specifiche con tag matchante (oggi nessuna
  // mossa del catalogo ha il tag `'magia'` formale; lasciato per estensione).
  const delta = config.levelAdjustment.delta;
  return Math.max(1, baseLevel + delta);
}

// ---------------------------------------------------------------------------
// Livelli accumulati per classe (`classLevels`) — derivato runtime
// ---------------------------------------------------------------------------

/**
 * **FI-17 (D7)** — Livelli accumulati per classe-multiclasse del PG.
 *
 * Derivato runtime da `character.levelUpHistory[].multiclassPick`. Non
 * persistito sul `Character` (decisione Q3=B 2026-05-14: SSOT history,
 * zero drift, zero migration).
 *
 * **Convenzione**: la classe primaria del PG (`character.classId`) NON
 * compare in questa mappa — il suo livello è `character.level` (lookup
 * separato). Solo le classi pescate via multiclasse appaiono.
 *
 * **Formula** (manuale rr. 1761-1768): per ogni classe X con almeno una
 * `multiclassPick.sourceClassId === X` in history:
 *  - `entryLevel[X]` = `levelReached` del primo record con quella source
 *  - `classLevels[X]` = `character.level - (entryLevel[X] - 1)`
 *
 * Esempi:
 *  - PG Guerriero L2: prende *Combattente Multiclasse* → mossa Mago
 *    (entry @ L2). Dopo applyLevelUp, character.level = 2,
 *    levelUpHistory[0].levelReached = 2 → `classLevels.mago = 2 - 1 = 1` ✓
 *  - Stesso PG sale a L3 (qualunque mossa scelga) → character.level = 3,
 *    entryLevel.mago resta 2 → `classLevels.mago = 3 - 1 = 2` ✓
 *  - PG che entra in 2 classi diverse (Mago @ L2, Chierico @ L4) a L5 →
 *    classLevels = { mago: 4, chierico: 2 }
 */
/**
 * **FI-57** — Livello effettivo del PG nella classe-source di una mossa,
 * usato dal pool filter spell di una grant durante level-up. Coerente con la
 * regola del manuale per multiclasse «considera te di livello inferiore ai
 * fini delle scelte» (vedi `MulticlassConfig.levelAdjustment`).
 *
 * **Casi**:
 *  - `ownClassId === character.classId` (classe primaria): ritorna
 *    `targetLevel` (nessun adjustment).
 *  - Classe pescata via multiclasse: ritorna `targetLevel - (entryLevel - 1)`
 *    dove `entryLevel` è il `levelReached` del primo record in
 *    `levelUpHistory` con `multiclassPick.sourceClassId === ownClassId`.
 *  - Classe non in `character.classId` né in history (edge case: mossa
 *    acquisita via canale non tracciato): fallback a `targetLevel`.
 *
 * **Esempi** (Guerriero L1 → multiclass Mago a L2 via Combattente
 * Multiclasse, levelAdjustment.delta=-1):
 *  - LU L2→L3 (targetLevel=3): effective Mago = `3 - (2-1) = 2`.
 *  - LU L5→L6 (targetLevel=6): effective Mago = `6 - (2-1) = 5`.
 *
 * **Differenza con `getClassLevels`**: questo helper accetta `targetLevel`
 * arbitrario (utile in editing level-up dove `character.level` è ancora il
 * livello PRE-salita), `getClassLevels` usa direttamente `character.level`.
 *
 * Min 1 (le mosse starting restano comunque disponibili).
 */
export function getEffectiveCharacterLevelForOwnClass(
  character: Character,
  ownClassId: ClassId,
  targetLevel: number,
): number {
  if (ownClassId === character.classId) return targetLevel;
  for (const record of character.levelUpHistory) {
    const pick = record.multiclassPick;
    if (pick?.sourceClassId === ownClassId) {
      return Math.max(1, targetLevel - (record.levelReached - 1));
    }
  }
  return targetLevel;
}

export function getClassLevels(character: Character): Readonly<Record<ClassId, number>> {
  const entryLevels: Partial<Record<ClassId, number>> = {};
  for (const record of character.levelUpHistory) {
    const pick = record.multiclassPick;
    if (!pick) continue;
    entryLevels[pick.sourceClassId] ??= record.levelReached;
  }
  const result: Partial<Record<ClassId, number>> = {};
  for (const classId of CLASS_IDS) {
    const entry = entryLevels[classId];
    if (entry !== undefined) {
      result[classId] = character.level - (entry - 1);
    }
  }
  return result as Readonly<Record<ClassId, number>>;
}

// ---------------------------------------------------------------------------
// Validazione di un input multiclassPick (consumato da applyLevelUp)
// ---------------------------------------------------------------------------

/**
 * Verifica che una mossa-target sia ammissibile dato il config + lo
 * stato del PG. Usato da `applyLevelUp` per `validateLevelUpInput`.
 *
 * Restituisce `null` se valida, una stringa d'errore se invalida.
 */
export function validateMulticlassPick(
  character: Character,
  triggerMove: Move,
  pick: { sourceClassId: ClassId; selectedMoveId: string },
  classDef: ClassDefinition,
): string | null {
  const config = triggerMove.multiclass;
  if (config === undefined) {
    return `La mossa "${triggerMove.id}" non è una mossa multiclasse (manca \`multiclass\`).`;
  }
  const classPool = getMulticlassClassPool(character, config, classDef);
  if (!classPool.includes(pick.sourceClassId)) {
    return `Classe "${pick.sourceClassId}" non nel pool multiclasse di "${triggerMove.id}". Pool: [${classPool.join(', ')}].`;
  }
  const movePool = getMulticlassMovePool(character, pick.sourceClassId, config, classDef);
  const found = movePool.find((m) => m.id === pick.selectedMoveId);
  if (!found) {
    return `Mossa "${pick.selectedMoveId}" non nel pool target di "${triggerMove.id}" (classe ${pick.sourceClassId}). Verifica prereq, tipo, già posseduta, livello effettivo.`;
  }
  // Lookup globale per coerenza: la mossa esiste nel catalogo cross-class
  const target = findMoveByIdGlobal(pick.selectedMoveId);
  if (target === undefined) {
    return `Mossa "${pick.selectedMoveId}" non nel catalogo globale.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Helper di re-export per coerenza con altri service
// ---------------------------------------------------------------------------

/**
 * Re-export di `arePrerequisitesSatisfied` per coerenza dei consumer.
 * Il prereq check è già SSOT in `levelUp.ts`.
 */
export { arePrerequisitesSatisfied };
export type { Prerequisite };
